import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Check, Trash2, Sparkles, BookOpen, TrendingUp, Target, Loader2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import { DictationButton } from '../components/DictationButton';

// Domain metadata with colors and icons
const DOMAIN_CONFIG = {
  physical_health: { color: '#4A90E2', angle: 0 },
  strength_fitness: { color: '#5BA3F5', angle: 30 },
  energy: { color: '#F5A623', angle: 60 },
  environment_order: { color: '#7B68EE', angle: 90 },
  work_mission: { color: '#D4AF37', angle: 120 },
  money_financial: { color: '#50C878', angle: 150 },
  love_relationship: { color: '#FF6B6B', angle: 180 },
  friends_community: { color: '#FF8C42', angle: 210 },
  emotional_stability: { color: '#9D7CBF', angle: 240 },
  discipline_consistency: { color: '#E24A4A', angle: 270 },
  learning_growth: { color: '#17BEBB', angle: 300 },
  meaning_purpose: { color: '#C9B037', angle: 330 },
};

const DOMAIN_LABELS = {
  en: {
    physical_health: 'Physical Health',
    strength_fitness: 'Strength / Fitness',
    energy: 'Energy',
    environment_order: 'Environment / Order',
    work_mission: 'Work / Mission',
    money_financial: 'Money / Financial',
    love_relationship: 'Love / Relationship',
    friends_community: 'Friends / Community',
    emotional_stability: 'Emotional Stability',
    discipline_consistency: 'Discipline / Consistency',
    learning_growth: 'Learning / Growth',
    meaning_purpose: 'Meaning / Purpose',
  },
  tr: {
    physical_health: 'Fiziksel Sağlık',
    strength_fitness: 'Güç / Fitness',
    energy: 'Enerji',
    environment_order: 'Çevre / Düzen',
    work_mission: 'İş / Misyon',
    money_financial: 'Para / Finansal',
    love_relationship: 'Aşk / İlişki',
    friends_community: 'Arkadaşlar / Topluluk',
    emotional_stability: 'Duygusal İstikrar',
    discipline_consistency: 'Disiplin / Tutarlılık',
    learning_growth: 'Öğrenme / Gelişim',
    meaning_purpose: 'Anlam / Amaç',
  },
};

// Domain-specific focus affirmation (one per domain) - Wise fallbacks
const DOMAIN_AFFIRMATIONS = {
  en: {
    physical_health: "Each breath is a choice to honor the vessel that carries my dreams.",
    strength_fitness: "My strength grows not from ease, but from meeting resistance with presence.",
    energy: "I am not tired; I am recalibrating.",
    environment_order: "Order is not control—it's creating space for what matters most.",
    work_mission: "Every task I complete is a brick in the cathedral I'm building.",
    money_financial: "Money flows toward those who respect its purpose and their own.",
    love_relationship: "Intimacy requires the courage to be seen as I truly am.",
    friends_community: "My tribe will find me when I stop hiding who I really am.",
    emotional_stability: "Peace is not the absence of storms, but knowing I can navigate them.",
    discipline_consistency: "I don't need motivation; my habits carry me when inspiration fades.",
    learning_growth: "The discomfort of not knowing is the sensation of growth.",
    meaning_purpose: "My purpose becomes clearer each time I act with intention.",
  },
  tr: {
    physical_health: "Her nefes, hayallerimi taşıyan bu bedeni onurlandırma seçimi.",
    strength_fitness: "Gücüm kolaylıktan değil, dirençle bilinçli karşılaşmadan geliyor.",
    energy: "Yorgun değilim; yeniden kalibre oluyorum.",
    environment_order: "Düzen kontrol değil—en önemli olana yer açmak.",
    work_mission: "Tamamladığım her görev inşa ettiğim katedralde bir tuğla.",
    money_financial: "Para, amacına ve kendine saygı duyanlara akar.",
    love_relationship: "Yakınlık, gerçek benliğimle görülme cesareti gerektirir.",
    friends_community: "Kabilem, gerçek kim olduğumu saklamayı bıraktığımda beni bulacak.",
    emotional_stability: "Huzur fırtınaların yokluğu değil, onları yönlendirebileceğimi bilmek.",
    discipline_consistency: "Motivasyona ihtiyacım yok; alışkanlıklarım ilham azaldığında beni taşıyor.",
    learning_growth: "Bilmemenin rahatsızlığı, büyümenin hissi.",
    meaning_purpose: "Amacım her bilinçli eylemle daha netleşiyor.",
  },
};

// Ray/Wedge SVG Component
const DomainRay = ({ score, color, angle }) => {
  const maxRadius = 120;
  const minRadius = 30;
  const scoreRadius = minRadius + ((score / 10) * (maxRadius - minRadius));
  const idealRadius = maxRadius;
  
  // Calculate wedge path (30 degree slice)
  const startAngle = (angle - 15) * (Math.PI / 180);
  const endAngle = (angle + 15) * (Math.PI / 180);
  
  const createWedgePath = (radius) => {
    const x1 = 150 + radius * Math.cos(startAngle - Math.PI / 2);
    const y1 = 150 + radius * Math.sin(startAngle - Math.PI / 2);
    const x2 = 150 + radius * Math.cos(endAngle - Math.PI / 2);
    const y2 = 150 + radius * Math.sin(endAngle - Math.PI / 2);
    
    return `M 150 150 L ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2} Z`;
  };
  
  return (
    <svg width="300" height="300" viewBox="0 0 300 300" className="mx-auto">
      {/* Background circles */}
      {[0.2, 0.4, 0.6, 0.8, 1].map((fraction, i) => (
        <circle
          key={i}
          cx="150"
          cy="150"
          r={minRadius + fraction * (maxRadius - minRadius)}
          fill="none"
          stroke="rgba(212, 175, 55, 0.1)"
          strokeWidth="1"
        />
      ))}
      
      {/* Ideal wedge (faded) */}
      <path
        d={createWedgePath(idealRadius)}
        fill={`${color}20`}
        stroke={color}
        strokeWidth="1"
        strokeOpacity="0.3"
      />
      
      {/* Current score wedge */}
      <motion.path
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        d={createWedgePath(scoreRadius)}
        fill={`${color}60`}
        stroke={color}
        strokeWidth="2"
      />
      
      {/* Center circle */}
      <circle cx="150" cy="150" r="25" fill="#030305" stroke={color} strokeWidth="2" />
      
      {/* Score text */}
      <text x="150" y="155" textAnchor="middle" fill={color} fontSize="20" fontWeight="bold">
        {score}
      </text>
    </svg>
  );
};

const DomainDetail = () => {
  const { domainKey } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [domainData, setDomainData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [addingTask, setAddingTask] = useState(false);
  const [resources, setResources] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingAi, setLoadingAi] = useState(false);
  const [affirmation, setAffirmation] = useState(null);
  const [affirmationLoading, setAffirmationLoading] = useState(false);
  
  const domainConfig = DOMAIN_CONFIG[domainKey] || { color: '#D4AF37', angle: 0 };
  const domainLabel = DOMAIN_LABELS[language]?.[domainKey] || domainKey;
  
  useEffect(() => {
    fetchDomainData();
    fetchAffirmation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainKey]);
  
  const fetchAffirmation = async () => {
    try {
      setAffirmationLoading(true);
      const response = await api.get(`/domain/${domainKey}/affirmation`);
      setAffirmation(response.data.affirmation);
    } catch (error) {
      console.error('Error fetching affirmation:', error);
    } finally {
      setAffirmationLoading(false);
    }
  };
  
  const fetchDomainData = async () => {
    try {
      setLoading(true);
      
      // Fetch domain-specific data
      const [historyRes, tasksRes, resourcesRes] = await Promise.all([
        api.get(`/domain/${domainKey}/history`),
        api.get(`/domain/${domainKey}/tasks`),
        api.get(`/inn/resources?domain=${domainKey}`),
      ]);
      
      setDomainData(historyRes.data);
      setTasks(tasksRes.data.tasks || []);
      setResources(resourcesRes.data.resources || []);
      
    } catch (error) {
      console.error('Error fetching domain data:', error);
      toast.error(language === 'tr' ? 'Veri yüklenemedi' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAiSuggestions = async () => {
    try {
      setLoadingAi(true);
      const response = await api.post(`/domain/${domainKey}/suggestions`, { language });
      setAiSuggestions(response.data.suggestions || []);
    } catch (error) {
      toast.error(language === 'tr' ? 'Öneriler yüklenemedi' : 'Failed to load suggestions');
    } finally {
      setLoadingAi(false);
    }
  };
  
  const addTask = async () => {
    if (!newTask.trim()) return;
    
    try {
      setAddingTask(true);
      const response = await api.post(`/domain/${domainKey}/tasks`, { 
        text: newTask.trim() 
      });
      setTasks([...tasks, response.data.task]);
      setNewTask('');
      toast.success(language === 'tr' ? 'Görev eklendi' : 'Task added');
    } catch (error) {
      toast.error(language === 'tr' ? 'Görev eklenemedi' : 'Failed to add task');
    } finally {
      setAddingTask(false);
    }
  };
  
  const toggleTask = async (taskId) => {
    try {
      const response = await api.patch(`/domain/${domainKey}/tasks/${taskId}/toggle`);
      setTasks(tasks.map(t => t.id === taskId ? response.data.task : t));
    } catch (error) {
      toast.error(language === 'tr' ? 'Görev güncellenemedi' : 'Failed to update task');
    }
  };
  
  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/domain/${domainKey}/tasks/${taskId}`);
      setTasks(tasks.filter(t => t.id !== taskId));
      toast.success(language === 'tr' ? 'Görev silindi' : 'Task deleted');
    } catch (error) {
      toast.error(language === 'tr' ? 'Görev silinemedi' : 'Failed to delete task');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }
  
  const currentScore = domainData?.current_score || 5;
  const history = domainData?.history || [];
  
  return (
    <div
      className="min-h-screen pb-36 lg:pb-24 px-4"
      style={{ position: 'relative', zIndex: 1, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.5rem)' }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8 pr-12">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 px-3 py-2 -ml-3 rounded-lg text-[#F8F5F2] hover:text-white hover:bg-white/5 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="uppercase tracking-widest text-xs font-sans">
              {language === 'tr' ? 'Panel' : 'Dashboard'}
            </span>
          </button>
          <h1
            className="font-serif text-2xl text-[#F8F5F2] truncate"
            style={{ color: domainConfig.color }}
          >
            {domainLabel}
          </h1>
        </div>
        
        {/* Ray Visualization */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="glass-panel rounded-2xl p-6 mb-6"
        >
          <DomainRay 
            score={currentScore} 
            color={domainConfig.color} 
            angle={domainConfig.angle} 
          />
          <p className="text-center text-[#A1A1AA] font-sans text-sm mt-4">
            {language === 'tr' ? 'Mevcut Skor' : 'Current Score'}: 
            <span className="text-[#D4AF37] font-semibold ml-2">{currentScore}/10</span>
          </p>
        </motion.div>
        
        {/* Affirmation / Focus Signal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="glass-panel rounded-2xl p-6 mb-6 border-l-4"
          style={{ borderLeftColor: domainConfig.color }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4" style={{ color: domainConfig.color }} />
            <h3 className="font-sans text-sm text-[#A1A1AA] uppercase tracking-wide">
              {language === 'tr' ? 'Odak Sinyali' : 'Focus Signal'}
            </h3>
          </div>
          {affirmationLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]" />
              <span className="text-[#A1A1AA] text-sm">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</span>
            </div>
          ) : affirmation ? (
            <p className="font-serif text-lg text-[#F8F5F2] italic leading-relaxed">
              "{affirmation}"
            </p>
          ) : (
            <p className="font-serif text-lg text-[#F8F5F2] italic leading-relaxed">
              "{DOMAIN_AFFIRMATIONS[language]?.[domainKey] || DOMAIN_AFFIRMATIONS.en[domainKey]}"
            </p>
          )}
        </motion.div>
        
        {/* Score History */}
        {history.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-panel rounded-2xl p-6 mb-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="font-sans text-lg text-[#F8F5F2]">
                {language === 'tr' ? 'Skor Geçmişi' : 'Score History'}
              </h2>
            </div>
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={history}>
                <XAxis 
                  dataKey="date" 
                  stroke="#6B7280" 
                  fontSize={10}
                  tickFormatter={(val) => new Date(val).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis domain={[0, 10]} stroke="#6B7280" fontSize={10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#0a0a0f', 
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke={domainConfig.color} 
                  strokeWidth={2}
                  dot={{ fill: domainConfig.color }}
                />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>
        )}
        
        {/* Tasks Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-panel rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="font-sans text-lg text-[#F8F5F2]">
                {language === 'tr' ? 'Görevlerim' : 'My Tasks'}
              </h2>
            </div>
            <span className="text-xs text-[#A1A1AA]">
              {tasks.filter(t => t.completed).length}/{tasks.length}
            </span>
          </div>
          
          {/* Add Task Input */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addTask()}
                placeholder={language === 'tr' ? 'Yeni görev ekle...' : 'Add new task...'}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 pr-10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none transition-colors font-sans text-sm"
                data-testid="new-task-input"
              />
              <div className="absolute top-1.5 right-1.5">
                <DictationButton onTranscript={(text) => setNewTask(prev => prev ? prev + ' ' + text : text)} />
              </div>
            </div>
            <button
              onClick={addTask}
              disabled={addingTask || !newTask.trim()}
              className="px-4 py-2 rounded-lg bg-[#D4AF37] text-[#030305] hover:bg-[#D4AF37]/90 disabled:opacity-50 transition-colors"
              data-testid="add-task-button"
            >
              {addingTask ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </button>
          </div>
          
          {/* Task List */}
          <div className="space-y-2">
            <AnimatePresence>
              {tasks.map((task) => (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/5 group"
                >
                  <button
                    onClick={() => toggleTask(task.id)}
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      task.completed 
                        ? 'bg-[#D4AF37] border-[#D4AF37]' 
                        : 'border-white/30 hover:border-[#D4AF37]'
                    }`}
                    data-testid={`toggle-task-${task.id}`}
                  >
                    {task.completed && <Check className="w-3 h-3 text-[#030305]" />}
                  </button>
                  <span className={`flex-1 font-sans text-sm ${task.completed ? 'text-[#6B7280] line-through' : 'text-[#F8F5F2]'}`}>
                    {task.text}
                  </span>
                  <button
                    onClick={() => deleteTask(task.id)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-all"
                    data-testid={`delete-task-${task.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {tasks.length === 0 && (
              <p className="text-center text-[#6B7280] text-sm py-4">
                {language === 'tr' ? 'Henüz görev eklenmedi' : 'No tasks yet'}
              </p>
            )}
          </div>
        </motion.div>
        
        {/* AI Suggestions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-panel rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="font-sans text-lg text-[#F8F5F2]">
                {language === 'tr' ? 'AI Önerileri' : 'AI Suggestions'}
              </h2>
            </div>
            {aiSuggestions.length === 0 && (
              <button
                onClick={fetchAiSuggestions}
                disabled={loadingAi}
                className="text-xs text-[#D4AF37] hover:text-[#D4AF37]/80 transition-colors flex items-center gap-1"
              >
                {loadingAi ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  language === 'tr' ? 'Önerileri Yükle' : 'Load Suggestions'
                )}
              </button>
            )}
          </div>
          
          {loadingAi ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
            </div>
          ) : aiSuggestions.length > 0 ? (
            <div className="space-y-3">
              {aiSuggestions.map((suggestion, index) => (
                <button 
                  key={index}
                  onClick={() => {
                    // Navigate to coaching with this suggestion as the starting topic
                    const domainLabel = DOMAIN_LABELS[language]?.[domainKey] || domainKey;
                    const prompt = language === 'tr'
                      ? `${domainLabel} alanındaki bu öneriyi tartışmak istiyorum: "${suggestion}"`
                      : `I'd like to discuss this suggestion for my ${domainLabel}: "${suggestion}"`;
                    navigate('/coaching', { state: { initialMessage: prompt } });
                  }}
                  className="w-full p-3 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 hover:bg-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all text-left group"
                  data-testid={`ai-suggestion-${index}`}
                >
                  <p className="text-sm text-[#F8F5F2] font-sans group-hover:text-[#D4AF37] transition-colors">{suggestion}</p>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-center text-[#6B7280] text-sm py-4">
              {language === 'tr' 
                ? 'Yapay zeka önerileri için butona tıklayın' 
                : 'Click button to load AI suggestions'}
            </p>
          )}
        </motion.div>
        
        {/* Related Resources from The INN */}
        {resources.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-panel rounded-2xl p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="font-sans text-lg text-[#F8F5F2]">
                {language === 'tr' ? 'İlgili Kaynaklar' : 'Related Resources'}
              </h2>
            </div>
            <div className="space-y-3">
              {resources.slice(0, 5).map((resource, index) => (
                <a
                  key={index}
                  href={resource.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <p className="text-sm text-[#F8F5F2] font-sans font-medium">{resource.title}</p>
                  <p className="text-xs text-[#A1A1AA] mt-1">{resource.author}</p>
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default DomainDetail;
